// Transitions for sections/quote-carousel.liquid. Liquid steps an SVG filter so the incoming
// quote grows out of blurred, wobbling droplets and settles into the font; the author line can
// share that effect or run its own fade.

const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
const THRESHOLD = 0.4;
const SHARPNESS = 18;
const EDGE_SOFTNESS = 2.5;

const AUTHOR_KEYFRAMES = {
  fade: [{ opacity: 0 }, { opacity: 1 }],
  rise: [
    { opacity: 0, transform: "translateY(0.75em)" },
    { opacity: 1, transform: "none" },
  ],
  blur: [
    { opacity: 0, filter: "blur(8px)" },
    { opacity: 1, filter: "blur(0)" },
  ],
};

const easeOut = (t) => 1 - Math.pow(1 - t, 3);
const lerp = (a, b, t) => a + (b - a) * t;
const smoothstep = (a, b, t) => {
  const x = Math.min(Math.max((t - a) / (b - a), 0), 1);
  return x * x * (3 - 2 * x);
};

// Wraps each letter in a span for the stagger. The split copy is hidden from assistive tech and
// the original text kept in a visually hidden span, so screen readers don't read letter by letter.
function splitLetters(el) {
  if (el.querySelector(".js-quote-letter")) return;
  const label = document.createElement("span");
  label.className = "visually-hidden";
  label.textContent = el.textContent;

  const split = document.createElement("span");
  split.setAttribute("aria-hidden", "true");
  el.childNodes.forEach((node) => {
    if (node.nodeType !== Node.TEXT_NODE) {
      split.append(node.cloneNode(true));
      return;
    }
    for (const char of node.textContent) {
      if (/\s/.test(char)) {
        split.append(char);
        continue;
      }
      const letter = document.createElement("span");
      letter.className = "js-quote-letter";
      letter.textContent = char;
      split.append(letter);
    }
  });
  el.replaceChildren(label, split);
}

class QuoteCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector("slide-section");
    if (!this.track) return;

    this.duration = this.read("duration", 1400);
    this.authorEffect = this.dataset.authorEffect || "none";
    this.stagger = this.hasAttribute("data-stagger");
    if (this.stagger) this.querySelectorAll(".js-quote-liquid").forEach(splitLetters);
    this.filters = [];
    if (this.dataset.transition === "liquid") {
      this.filters.push(this.liquidLayer(this.dataset.filterId, ".js-quote-liquid"));
      if (this.authorEffect === "match") {
        this.filters.push(this.liquidLayer(`${this.dataset.filterId}-author`, ".js-quote-author"));
      }
      this.filters = this.filters.filter(Boolean);
    }

    const animated = this.filters.map((layer) => layer.selector);
    if (this.stagger) animated.push(".js-quote-liquid");
    if (AUTHOR_KEYFRAMES[this.authorEffect]) animated.push(".js-quote-author");
    this.animatedSelector = [...new Set(animated)].join(",");

    this.onSlideChange = this.onSlideChange.bind(this);
    this.onSliderMove = this.onSliderMove.bind(this);
    this.release = this.release.bind(this);
    customElements.whenDefined("slide-section").then(() => {
      this.swiper = this.track.swiper;
      this.realIndex = this.swiper?.realIndex;
      this.swiper?.on("slideChange", this.onSlideChange);
      if (this.animatedSelector) {
        this.swiper?.on("sliderMove", this.onSliderMove);
        this.swiper?.on("transitionEnd", this.release);
      }
    });
  }

  disconnectedCallback() {
    this.stop();
    this.release();
    this.swiper?.off("slideChange", this.onSlideChange);
    this.swiper?.off("sliderMove", this.onSliderMove);
    this.swiper?.off("transitionEnd", this.release);
  }

  // Swiper's fade mode crossfades in step with a drag, which would show the next quote as plain
  // text before the effect starts on release, so animated text on other slides is held hidden.
  onSliderMove() {
    if (this.held || reducedMotion.matches) return;
    const active = this.swiper.slides[this.swiper.activeIndex];
    this.held = [...this.swiper.slides]
      .filter((slide) => slide !== active)
      .flatMap((slide) => [...slide.querySelectorAll(this.animatedSelector)]);
    this.held.forEach((el) => (el.style.opacity = "0"));
  }

  release() {
    this.held?.forEach((el) => (el.style.opacity = ""));
    this.held = null;
  }

  read(key, fallback) {
    const value = parseFloat(this.dataset[key]);
    return Number.isNaN(value) ? fallback : value;
  }

  liquidLayer(id, selector) {
    const filter = document.getElementById(id);
    if (!filter) return null;
    return {
      selector,
      url: `url(#${id})`,
      noise: filter.querySelector("feTurbulence"),
      drift: filter.querySelector("feOffset"),
      warp: filter.querySelector("feDisplacementMap"),
      bleed: filter.querySelector("feGaussianBlur"),
      alpha: filter.querySelector("feColorMatrix"),
    };
  }

  // In loop mode Swiper also fires slideChange when it reorders slides at the start of a drag,
  // so only a change of realIndex counts as a new quote.
  onSlideChange() {
    if (this.swiper.realIndex === this.realIndex) return;
    this.realIndex = this.swiper.realIndex;
    this.stop();
    if (reducedMotion.matches) return;

    const slide = this.swiper.slides[this.swiper.activeIndex];
    if (!slide) return;

    this.runs = this.filters.map((layer) => this.startLiquid(layer, slide)).filter(Boolean);

    const letters = this.stagger ? slide.querySelectorAll(".js-quote-letter") : [];
    // Letters finish by 30% of the duration, while the liquid blur is still clearing.
    const step = (this.duration * 0.18) / Math.max(letters.length - 1, 1);
    this.letterAnimations = [...letters].map((letter, i) =>
      letter.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: this.duration * 0.12,
        delay: i * step,
        easing: "ease-out",
        fill: "backwards",
      })
    );
    if (this.runs.length) {
      this.start = performance.now();
      this.frame = requestAnimationFrame(this.tick.bind(this));
    }

    const keyframes = AUTHOR_KEYFRAMES[this.authorEffect];
    const author = slide.querySelector(".js-quote-author");
    if (keyframes && author) {
      this.authorAnimation = author.animate(keyframes, {
        duration: this.duration * 0.65,
        delay: this.duration * 0.35,
        easing: "cubic-bezier(0.22, 1, 0.36, 1)",
        fill: "backwards",
      });
    }

    this.release();
  }

  // Blur and wobble scale with the target's own font size, so the small author line gets
  // droplets proportional to its strokes instead of vanishing under the quote's blur.
  startLiquid(layer, slide) {
    const target = slide.querySelector(layer.selector);
    if (!target) return null;

    const fontSize = parseFloat(getComputedStyle(target).fontSize) || 48;
    const run = {
      layer,
      target,
      blur: fontSize * 0.05 * this.read("bleed", 5),
      wobble: fontSize * 0.04 * this.read("bubbling", 4),
      rise: fontSize * 0.6,
    };
    layer.noise.setAttribute("baseFrequency", (1 / (fontSize * 0.06 * this.read("blotch", 5))).toFixed(4));
    this.paint(run, 0);
    target.style.filter = layer.url;
    return run;
  }

  tick(now) {
    const t = Math.min((now - this.start) / this.duration, 1);
    this.runs.forEach((run) => this.paint(run, t));
    if (t < 1) {
      this.frame = requestAnimationFrame(this.tick.bind(this));
    } else {
      this.stop();
    }
  }

  // Blur decays against a fixed alpha threshold, so the thickest strokes cross it first as
  // droplets. Sharpness and wobble are tied to the remaining blur: a hard threshold over less
  // than ~1px of blur strips antialiasing and leaves jagged edges.
  paint({ layer, blur, wobble, rise }, t) {
    const remaining = 1 - easeOut(t);
    const radius = blur * remaining;
    const opacity = lerp(0.35, 1, smoothstep(0, 0.6, t));
    const k = Math.min(Math.max(EDGE_SOFTNESS * radius, 1), SHARPNESS);

    layer.bleed.setAttribute("stdDeviation", radius.toFixed(2));
    layer.warp.setAttribute("scale", (wobble * remaining).toFixed(2));
    layer.drift.setAttribute("dy", (-rise * t).toFixed(2));
    layer.alpha.setAttribute(
      "values",
      `1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 ${(opacity * k).toFixed(3)} ${(-opacity * (k - 1) * THRESHOLD).toFixed(3)}`
    );
  }

  stop() {
    cancelAnimationFrame(this.frame);
    this.runs?.forEach((run) => (run.target.style.filter = ""));
    this.runs = [];
    this.authorAnimation?.cancel();
    this.authorAnimation = null;
    this.letterAnimations?.forEach((animation) => animation.cancel());
    this.letterAnimations = [];
  }
}

if (!customElements.get("quote-carousel")) {
  customElements.define("quote-carousel", QuoteCarousel);
}
